import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, MapPin, Star, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesList = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Fehler beim Laden der Favoriten');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (providerId, providerName) => {
    if (!window.confirm(`Möchtest du "${providerName}" wirklich aus deinen Favoriten entfernen?`)) {
      return;
    }

    setRemoving(providerId);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/favorites/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFavorites(favorites.filter(fav => fav.provider.id !== providerId));
      toast.success('Aus Favoriten entfernt');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Fehler beim Entfernen');
    } finally {
      setRemoving(null);
    }
  };

  const handleViewProfile = (providerId) => {
    navigate(`/provider/${providerId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Noch keine Favoriten
        </h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Du hast noch keine Favoriten. Markiere interessante Dienstleister mit dem Herz-Symbol, 
          um sie hier später schnell wiederzufinden.
        </p>
        <button
          onClick={() => navigate('/search')}
          className="px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl transition-colors"
        >
          Dienstleister entdecken
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Meine Favoriten ({favorites.length})
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {favorites.map(({ favorite_id, provider, created_at }) => (
          <div
            key={favorite_id}
            className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
          >
            {/* Image */}
            <div className="relative h-48 bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2]">
              {(() => {
                const displayImage = provider.profile_picture || (provider.images && provider.images.length > 0 ? provider.images[0] : null);
                return displayImage ? (
                  <img
                    src={getImageUrl(displayImage)}
                    alt={provider.company_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center"><p class="text-4xl font-bold text-gray-300">${provider.company_name?.charAt(0) || '?'}</p></div>`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Heart className="w-12 h-12 text-gray-400" />
                  </div>
                );
              })()}
              <div className="absolute top-3 right-3">
                <Heart
                  className="w-6 h-6 text-red-500 fill-current drop-shadow-lg"
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <div className="mb-3">
                <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1">
                  {provider.company_name}
                </h3>
                {provider.categories && provider.categories.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {provider.categories[0]}
                  </p>
                )}
              </div>

              {provider.location_city && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <MapPin className="w-4 h-4" />
                  <span>{provider.location_city}</span>
                </div>
              )}

              {provider.average_rating > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-semibold text-gray-900">
                    {provider.average_rating.toFixed(1)}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({provider.total_reviews} Bewertungen)
                  </span>
                </div>
              )}

              {provider.short_description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {provider.short_description}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewProfile(provider.id)}
                  className="flex-1 px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Profil ansehen
                </button>
                <button
                  onClick={() => handleRemove(provider.id, provider.company_name)}
                  disabled={removing === provider.id}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors disabled:opacity-50"
                  title="Aus Favoriten entfernen"
                >
                  {removing === provider.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FavoritesList;
